// src/services/notificationsService.ts
//
// All Supabase access for the in-app notification bell (Story 4.9) is isolated
// here. The service reads the caller's in_app notifications and marks them read —
// nothing else. Layering (spine AD-3): components/hooks never touch Supabase;
// they consume the clean `src/types/yak.ts` shapes. Results are cast once per
// method at the boundary. Reference chain: regionsService → useRegions →
// RegionManagement; here notificationsService → useNotifications → NotificationBell.
//
// THE WRITE CONTRACT (see database/040_yak_notifications_read_policy.sql header):
//   * The ONLY column this service ever writes is `read_at`. Its .update()
//     payloads NEVER touch status/sent_at/last_error/attempts (3.7's delivery
//     reaction owns those, on email rows). This is unit-tested.
//   * Mark-read is a DIRECT-UNDER-RLS write, NOT a tx_ transition (no envelope,
//     no unwrapTx) — it rides the recipient-scoped in_app-only UPDATE policy 3.5
//     AC 2 reserved and 040 shipped. read_at is set ONCE (.is('read_at', null));
//     no unmark path exists. 3.5's column guard freezes every other column.
//
// THE READ CONTRACT: the _org_isolation SELECT policy stays org-scoped
// (deliberately — the read-model decision in the 040 header). The per-user bell
// is a QUERY PREDICATE: every read filters recipient_user_id to the session user
// and channel to 'in_app'. A dropped filter would be a correctness bug (a
// colleague's pings), never a tenancy breach.

import { supabase } from '../lib/supabase'
import type { YakNotificationWithTicket } from '../types/yak'

// The panel shows a capped, non-paginated window; the head-count query keeps the
// badge exact beyond this cap (Decision #4). Named constant, single source.
export const BELL_LIST_LIMIT = 20

// The ticket embed rides the composite FK yak_notifications_ticket_fkey
// (ticket_id, organization_id) → yak_tickets (id, organization_id), reconciled
// from 3.5's landed migration + generated types. Naming the constraint
// disambiguates it. Only the columns the panel needs are projected.
const NOTIFICATION_SELECT =
  '*, ticket:yak_tickets!yak_notifications_ticket_fkey(id, question, question_type, status)'

class NotificationsService {
  // The session user id, or null when no session (the bell renders empty rather
  // than leaking the org — the recipient predicate is the point of this feed).
  private async sessionUserId(): Promise<string | null> {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    return session?.user?.id ?? null
  }

  // The caller's 20 most recent in_app notifications, newest-first (created_at
  // desc, id tiebreak), read + unread together, each with its ticket embed. The
  // explicit recipient_user_id filter is the per-user predicate under the org-wide
  // SELECT policy (deliberate — see the read contract).
  async listMine(): Promise<YakNotificationWithTicket[]> {
    const userId = await this.sessionUserId()
    if (!userId) return []

    const { data, error } = await supabase
      .from('yak_notifications')
      .select(NOTIFICATION_SELECT)
      .eq('channel', 'in_app')
      .eq('recipient_user_id', userId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(BELL_LIST_LIMIT)

    if (error) throw new Error(error.message)
    return (data as unknown as YakNotificationWithTicket[] | null) ?? []
  }

  // The caller's unread in_app count — a head-only exact count (no rows
  // transferred), so the badge stays exact beyond the list cap.
  async unreadCount(): Promise<number> {
    const userId = await this.sessionUserId()
    if (!userId) return 0

    const { count, error } = await supabase
      .from('yak_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('channel', 'in_app')
      .eq('recipient_user_id', userId)
      .is('read_at', null)

    if (error) throw new Error(error.message)
    return count ?? 0
  }

  // Mark one row read. read_at is set ONCE: the .is('read_at', null) filter means
  // a re-mark (already read / raced) updates zero rows — that is SUCCESS, not an
  // error (first-read time is never overwritten). The RLS policy scopes the write
  // to the caller's own in_app row; the payload carries read_at ALONE.
  async markRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('yak_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
      .is('read_at', null)

    if (error) throw new Error(error.message)
  }

  // Mark all of the caller's unread in_app rows read in one UPDATE. The explicit
  // recipient/channel filters make the intent legible in the query even though the
  // policy already scopes the write; the payload carries read_at ALONE.
  async markAllRead(): Promise<void> {
    const userId = await this.sessionUserId()
    if (!userId) return

    const { error } = await supabase
      .from('yak_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('recipient_user_id', userId)
      .eq('channel', 'in_app')
      .is('read_at', null)

    if (error) throw new Error(error.message)
  }
}

export const notificationsService = new NotificationsService()
